import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DOMPurify from "dompurify";
import { 
  FiUser, 
  FiSettings, 
  FiLogOut, 
  FiCheck, 
  FiEdit2,
  FiSave,
  FiLoader,
  FiMail,
  FiPhone,
  FiShield,
  FiKey,
  FiCalendar,
  FiAward,
  FiUsers,
  FiFileText,
  FiBell,
  FiGlobe,
  FiMenu,
  FiX,
  FiCamera,
  FiTrash2
} from "react-icons/fi";
import { 
  HiOutlineUserCircle,
  HiOutlineCog,
  HiOutlineShieldCheck,
  HiOutlineIdentification
} from "react-icons/hi";

// Professional color scheme - consistent and accessible
const colors = {
  primary: '#2563eb',
  secondary: '#475569',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  lightBg: '#f8fafc',
  cardBg: '#ffffff',
  border: '#e2e8f0',
  textDark: '#1e293b',
  textMedium: '#475569',
  textLight: '#64748b',
  gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
};

const AdminProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: ""
  });
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();
  const { logout } = useAuth();

  // API Base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.tinubu-ambode.org";

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Input sanitization function
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return DOMPurify.sanitize(input.trim());
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Profile picture functions
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Convert image to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setProfileImage(base64String);
        
        // Save to localStorage
        if (userData?.membership_id) {
          localStorage.setItem(`adminProfilePic_${userData.membership_id}`, base64String);
        }
        
        setUploading(false);
      };
      reader.onerror = () => {
        setError('Error reading image file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Error uploading image');
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    if (userData?.membership_id) {
      localStorage.removeItem(`adminProfilePic_${userData.membership_id}`);
    }
  };

  const getProfileImageSrc = () => {
    if (profileImage) {
      return profileImage;
    }
    
    // Return server image if available
    if (userData?.profile_photo) {
      return userData.profile_photo.startsWith('http') 
        ? userData.profile_photo 
        : `${API_BASE_URL}${userData.profile_photo}`;
    }
    
    if (userData?.files?.passport_photo) {
      return userData.files.passport_photo.startsWith('http')
        ? userData.files.passport_photo
        : `${API_BASE_URL}${userData.files.passport_photo}`;
    }
    
    // Return default if no profile image
    return null;
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'AD';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem("authToken");

        if (!token) {
          navigate("/login");
          return;
        }

        // Fetch user data from API
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/profile`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.status === "success" && data.data.user) {
          const user = data.data.user;
          
          // Validate and sanitize user data
          const sanitizedData = {
            ...user,
            full_name: sanitizeInput(user.full_name || ''),
            email: validateEmail(user.email) ? sanitizeInput(user.email) : '',
            membership_id: sanitizeInput(user.membership_id || ''),
            role: 'admin', // Assuming admin role for this component
            permissions: user.permissions || ['user-management', 'content-moderation', 'system-config', 'analytics'],
            status: user.status || 'approved',
            membership_category: user.membership_category || 'Volunteer',
            geographic_info: user.geographic_info || {}
          };

          setUserData(sanitizedData);

          // Initialize form data with API response
          setFormData({
            first_name: user.first_name || '',
            surname: user.surname || '',
            other_name: user.other_name || '',
            email: user.email || '',
            telephone_numbers: user.telephone_numbers || [''],
            whatsapp_number: user.whatsapp_number || '',
            date_of_birth: user.date_of_birth || '',
            gender: user.gender || '',
            full_house_address: user.full_house_address || '',
            state_code: user.state_code || '',
            sql_code: user.sql_code || '',
            fc_code: user.fc_code || '',
            lcda_code: user.lcda_code || '',
            lg_code: user.lg_code || '',
            ward_code: user.ward_code || '',
            pu_code: user.pu_code || '',
            membership_category: user.membership_category || ''
          });

          // Update localStorage with fresh data
          localStorage.setItem("userData", JSON.stringify(sanitizedData));

          // Load profile picture from localStorage if exists, otherwise use server image
          const savedProfilePic = localStorage.getItem(`adminProfilePic_${sanitizedData.membership_id}`);
          if (savedProfilePic) {
            setProfileImage(savedProfilePic);
          }
        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        
        // Fallback to localStorage data if API fails
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
          try {
            const parsedData = JSON.parse(storedUserData);
            setUserData(parsedData);
            
            const nameParts = parsedData.full_name?.split(' ') || ['', ''];
            setFormData({
              first_name: nameParts[0] || '',
              surname: nameParts.slice(1).join(' ') || '',
              email: parsedData.email || '',
              telephone_numbers: parsedData.telephone_numbers || [''],
              whatsapp_number: parsedData.whatsapp_number || '',
              date_of_birth: parsedData.date_of_birth || '',
              gender: parsedData.gender || '',
              full_house_address: parsedData.full_house_address || '',
              state_code: parsedData.state_code || '',
              sql_code: parsedData.sql_code || '',
              fc_code: parsedData.fc_code || '',
              lcda_code: parsedData.lcda_code || '',
              lg_code: parsedData.lg_code || '',
              ward_code: parsedData.ward_code || '',
              pu_code: parsedData.pu_code || '',
              membership_category: parsedData.membership_category || ''
            });
            
            setError("Using cached data - unable to fetch fresh data from server");
          } catch (parseError) {
            setError("Failed to load profile data. Please try again.");
          }
        } else {
          setError("Failed to load profile data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      handleSaveProfile();
    } else {
      setIsEditing(true);
      // Reset password fields when entering edit mode
      setPasswordData({
        current_password: "",
        password: "",
        password_confirmation: ""
      });
      setPasswordError("");
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    setSaveLoading(true);
    setPasswordError("");

    try {
      // Prepare update data - mapping to match API field names
      const updateData = {
        first_name: formData.first_name,
        surname: formData.surname,
        other_name: formData.other_name,
        email: formData.email,
        telephone_numbers: formData.telephone_numbers,
        whatsapp_number: formData.whatsapp_number,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        full_house_address: formData.full_house_address,
        state_code: formData.state_code,
        sql_code: formData.sql_code,
        fc_code: formData.fc_code,
        lcda_code: formData.lcda_code,
        lg_code: formData.lg_code,
        ward_code: formData.ward_code,
        pu_code: formData.pu_code,
        membership_category: formData.membership_category
      };

      // Add password data only if user wants to change password
      if (passwordData.password) {
        if (passwordData.password !== passwordData.password_confirmation) {
          setPasswordError("New password and confirmation do not match");
          setSaveLoading(false);
          return;
        }
        if (passwordData.password.length < 6) {
          setPasswordError("Password must be at least 6 characters long");
          setSaveLoading(false);
          return;
        }
        
        updateData.current_password = passwordData.current_password;
        updateData.password = passwordData.password;
        updateData.password_confirmation = passwordData.password_confirmation;
      }

      // Use the correct endpoint and method
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        // Try to parse error response
        const errorText = await response.text();
        console.error("Error response:", errorText);
        
        // Check if it's HTML error page
        if (errorText.startsWith('<!DOCTYPE') || errorText.startsWith('<html')) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        
        // Try to parse as JSON
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (data.status === "success") {
        // Update local state with new data
        const updatedUser = { 
          ...userData, 
          ...formData,
          full_name: `${formData.first_name} ${formData.surname}`.trim()
        };
        setUserData(updatedUser);
        localStorage.setItem("userData", JSON.stringify(updatedUser));
        setIsEditing(false);
        
        // Reset password fields
        setPasswordData({
          current_password: "",
          password: "",
          password_confirmation: ""
        });
        
        alert("Profile updated successfully!");
      } else {
        throw new Error(data.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      if (error.message.includes("password") || error.message.includes("current")) {
        setPasswordError(error.message);
      } else {
        alert("Failed to update profile: " + error.message);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (passwordError) {
      setPasswordError("");
    }
  };

  const handlePhoneNumberChange = (index, value) => {
    const updatedNumbers = [...(formData.telephone_numbers || [])];
    updatedNumbers[index] = value;
    setFormData(prev => ({
      ...prev,
      telephone_numbers: updatedNumbers
    }));
  };

  const handleLogout = () => {
    setShowLogoutSuccess(true);
    setTimeout(() => {
      logout();
      navigate("/login");
    }, 2000);
  };

  // Responsive styles based on screen size
  const styles = {
    profileContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px 12px' : '30px 20px',
      background: colors.lightBg,
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflowX: 'hidden'
    },
    skeletonHeader: {
      height: isMobile ? '80px' : '120px',
      background: colors.cardBg,
      borderRadius: '16px',
      marginBottom: '16px',
      animation: 'pulse 2s infinite'
    },
    skeletonCard: {
      height: isMobile ? '150px' : '200px',
      background: colors.cardBg,
      borderRadius: '16px',
      marginBottom: '16px',
      animation: 'pulse 2s infinite'
    },
    errorMessage: {
      background: '#fef2f2',
      color: colors.error,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      textAlign: 'center',
      margin: isMobile ? '20px 0' : '40px 20px',
      border: `1px solid ${colors.error}20`,
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '500'
    },
    errorBanner: {
      background: '#fef2f2',
      color: colors.error,
      padding: isMobile ? '12px' : '16px',
      borderRadius: '8px',
      textAlign: 'center',
      marginBottom: '16px',
      border: `1px solid ${colors.error}20`,
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '500'
    },
    logoutModalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(8px)',
      padding: isMobile ? '16px' : '0'
    },
    logoutModal: {
      background: colors.cardBg,
      padding: isMobile ? '24px' : '40px',
      borderRadius: '20px',
      textAlign: 'center',
      maxWidth: '440px',
      width: '100%',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      border: `1px solid ${colors.border}`,
      margin: isMobile ? '16px' : '0'
    },
    profileHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '32px' : '48px',
      padding: isMobile ? '40px 0 20px' : '60px 0 40px'
    },
    profileTitle: {
      color: colors.textDark,
      margin: '0 0 12px 0',
      fontSize: isMobile ? '1.75rem' : '2.5rem',
      fontWeight: '700',
      background: colors.gradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      lineHeight: 1.2
    },
    profileSubtitle: {
      color: colors.textMedium,
      fontSize: isMobile ? '1rem' : '1.125rem',
      fontWeight: '500',
      margin: 0,
      lineHeight: 1.4
    },
    card: {
      background: colors.cardBg,
      borderRadius: isMobile ? '12px' : '20px',
      padding: isMobile ? '20px' : '32px',
      marginBottom: isMobile ? '16px' : '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      border: `1px solid ${colors.border}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    membershipStatus: {
      background: colors.gradient,
      color: colors.cardBg,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
      gap: isMobile ? '16px' : '24px',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      textAlign: isMobile ? 'center' : 'left'
    },
    statusPattern: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
      pointerEvents: 'none'
    },
    profileImageContainer: {
      position: 'relative',
      display: 'inline-block'
    },
    profileImg: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '50%',
      border: `3px solid rgba(255,255,255,0.3)`,
      objectFit: 'cover',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      margin: isMobile ? '0 auto' : '0'
    },
    profileImgPlaceholder: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '50%',
      border: `3px solid rgba(255,255,255,0.3)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1rem' : '1.25rem',
      fontWeight: 'bold',
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      margin: isMobile ? '0 auto' : '0',
      color: 'rgba(255,255,255,0.9)'
    },
    profileImageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      borderRadius: '50%',
      display: isEditing ? 'flex' : 'none',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      opacity: isEditing ? 1 : 0,
      transition: 'all 0.3s ease',
      gap: '4px'
    },
    uploadBtn: {
      background: colors.primary,
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '10px',
      transition: 'all 0.3s ease',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '2px'
    },
    removeBtn: {
      background: colors.error,
      color: 'white',
      border: 'none',
      padding: '4px',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '8px',
      transition: 'all 0.3s ease',
      width: '20px',
      height: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    profileName: {
      margin: '0 0 8px 0',
      fontSize: isMobile ? '1.25rem' : '1.75rem',
      fontWeight: '700',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'flex-start',
      gap: isMobile ? '8px' : '12px'
    },
    adminBadge: {
      background: 'rgba(255, 255, 255, 0.2)',
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.3)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      width: isMobile ? 'fit-content' : 'auto'
    },
    sectionHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      paddingBottom: isMobile ? '12px' : '16px',
      borderBottom: `1px solid ${colors.border}`
    },
    sectionTitle: {
      margin: '0',
      color: colors.textDark,
      fontSize: isMobile ? '1.125rem' : '1.25rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px'
    },
    sectionIcon: {
      color: colors.accent,
      fontSize: isMobile ? '18px' : '20px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: isMobile ? '16px' : '20px'
    },
    inputGroup: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textLight,
      fontSize: isMobile ? '16px' : '18px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '500',
      color: colors.textDark,
      fontSize: isMobile ? '0.8rem' : '0.875rem'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px 12px 10px 36px' : '12px 16px 12px 40px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      transition: 'all 0.2s ease',
      backgroundColor: isEditing ? colors.cardBg : colors.lightBg,
      color: colors.textDark
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '10px 12px' : '12px 16px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      transition: 'all 0.2s ease',
      backgroundColor: isEditing ? colors.cardBg : colors.lightBg,
      color: colors.textDark,
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '80px'
    },
    adminInfoGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: isMobile ? '16px' : '24px'
    },
    permissionsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '12px',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    permissionTag: {
      background: colors.gradient,
      color: colors.cardBg,
      padding: isMobile ? '4px 10px' : '6px 12px',
      borderRadius: '16px',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    cardActions: {
      display: 'flex',
      gap: isMobile ? '12px' : '16px',
      marginTop: isMobile ? '24px' : '32px',
      justifyContent: 'center',
      flexDirection: isMobile ? 'column' : 'row'
    },
    editBtn: {
      background: colors.gradient,
      color: colors.cardBg,
      border: 'none',
      padding: isMobile ? '10px 20px' : '12px 32px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    cancelBtn: {
      background: colors.secondary,
      color: colors.cardBg,
      border: 'none',
      padding: isMobile ? '10px 20px' : '12px 32px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    logoutContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: isMobile ? '32px' : '48px',
      padding: isMobile ? '24px 0' : '40px 0',
      borderTop: `1px solid ${colors.border}`
    },
    logoutBtn: {
      background: 'transparent',
      color: colors.error,
      border: `1px solid ${colors.error}`,
      padding: isMobile ? '10px 20px' : '12px 32px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      minWidth: isMobile ? '100%' : '140px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    membershipCardContent: {
      background: colors.gradient,
      color: colors.cardBg,
      padding: isMobile ? '20px' : '32px',
      borderRadius: '12px',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
      gap: isMobile ? '20px' : '32px',
      alignItems: 'center',
      textAlign: isMobile ? 'center' : 'left'
    },
    membershipIdSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: isMobile ? 'center' : 'flex-start',
      marginBottom: '12px'
    },
    membershipBadges: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    statusBadge: {
      background: 'rgba(255, 255, 255, 0.2)',
      padding: '6px 12px',
      borderRadius: '16px',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }
  };

  // Loading Skeleton Component
  const LoadingSkeleton = () => (
    <div style={styles.profileContainer}>
      <div style={styles.skeletonHeader}></div>
      <div style={styles.skeletonCard}></div>
      <div style={styles.skeletonCard}></div>
      <div style={styles.skeletonCard}></div>
    </div>
  );

  // Permission icons mapping
  const permissionIcons = {
    'user-management': <FiUsers size={12} />,
    'content-moderation': <FiFileText size={12} />,
    'system-config': <FiSettings size={12} />,
    'analytics': <FiGlobe size={12} />,
    'security': <FiShield size={12} />,
    'notifications': <FiBell size={12} />
  };

  const getPermissionIcon = (permission) => {
    const key = permission.toLowerCase();
    return permissionIcons[key] || <FiKey size={12} />;
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error && !userData) {
    return (
      <div style={styles.profileContainer}>
        <div style={styles.errorMessage}>
          {error}
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginLeft: '12px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: isMobile ? 'block' : 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: isMobile ? '8px' : '0',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FiLoader size={14} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={styles.profileContainer}>
        <div style={styles.errorMessage}>
          No user data found. Please login again.
          <button 
            onClick={() => navigate('/login')}
            style={{
              marginLeft: isMobile ? '0' : '12px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: isMobile ? 'block' : 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: isMobile ? '8px' : '0',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FiLogOut size={14} />
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.profileContainer}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @keyframes progressFill {
            0% { width: 0%; }
            100% { width: 100%; }
          }

          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .btn-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(37, 99, 235, 0.3);
          }

          .logout-btn-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
            background: #ef4444;
            color: white;
          }

          input:focus, textarea:focus, select:focus {
            outline: none;
            border-color: ${colors.accent};
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .fade-in {
            animation: fadeIn 0.6s ease-out;
          }

          .profile-image-hover:hover .profile-image-overlay {
            opacity: 1;
          }

          /* Mobile-specific optimizations */
          @media (max-width: 480px) {
            .mobile-text-sm {
              font-size: 0.875rem;
            }
            
            .mobile-p-0 {
              padding: 0;
            }
          }

          /* Prevent horizontal scrolling */
          body {
            overflow-x: hidden;
          }
        `}
      </style>

      {/* Logout Success Modal */}
      {showLogoutSuccess && (
        <div style={styles.logoutModalOverlay} className="fade-in">
          <div style={styles.logoutModal}>
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              background: colors.gradient,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: isMobile ? '20px' : '24px',
              color: colors.cardBg
            }}>
              <FiCheck size={isMobile ? 20 : 24} />
            </div>
            <h3 style={{ 
              color: colors.textDark, 
              margin: '0 0 12px 0', 
              fontSize: isMobile ? '18px' : '20px', 
              fontWeight: '600' 
            }}>
              Logged Out Successfully
            </h3>
            <p style={{ 
              color: colors.textMedium, 
              margin: '0 0 20px 0', 
              fontSize: isMobile ? '13px' : '14px' 
            }}>
              You have been securely logged out. Redirecting to login...
            </p>
            <div style={{
              width: '100%',
              height: '4px',
              background: colors.border,
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: colors.gradient,
                borderRadius: '2px',
                animation: 'progressFill 2s ease-in-out',
                width: '100%'
              }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div style={styles.errorBanner} className="fade-in">
          {error}
        </div>
      )}

      {/* Membership Status */}
      <div style={{...styles.card, ...styles.membershipStatus}} className="card-hover fade-in">
        <div style={styles.statusPattern}></div>
        <div style={styles.profileImageContainer} className="profile-image-hover">
          {getProfileImageSrc() ? (
            <img 
              src={getProfileImageSrc()} 
              alt="profile" 
              style={styles.profileImg}
            />
          ) : (
            <div style={styles.profileImgPlaceholder}>
              {getInitials(userData.full_name)}
            </div>
          )}
          {isEditing && (
            <div style={styles.profileImageOverlay}>
              <label htmlFor="admin-profile-upload" style={styles.uploadBtn}>
                <FiCamera size={8} />
                {uploading ? '...' : 'Change'}
              </label>
              {getProfileImageSrc() && (
                <button 
                  style={styles.removeBtn}
                  onClick={handleRemoveImage}
                  disabled={uploading}
                >
                  <FiTrash2 size={8} />
                </button>
              )}
              <input
                id="admin-profile-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>
        <div>
          <h3 style={styles.profileName}>
            {userData.full_name} 
            <span style={styles.adminBadge}>
              <HiOutlineShieldCheck size={isMobile ? 10 : 12} />
              {userData.role?.replace('-', ' ').toUpperCase() || 'ADMIN'}
            </span>
          </h3>
          <p style={{ 
            margin: 0, 
            opacity: 0.9, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            justifyContent: isMobile ? 'center' : 'flex-start',
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}>
            <FiMail size={isMobile ? 12 : 14} />
            {userData.email}
          </p>
        </div>
        <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
          <p style={{ 
            margin: '4px 0', 
            opacity: 0.9, 
            fontSize: isMobile ? '0.75rem' : '0.875rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px', 
            justifyContent: isMobile ? 'center' : 'flex-end' 
          }}>
            <FiAward size={isMobile ? 12 : 14} />
            ID: {userData.membership_id}
          </p>
          <div style={styles.statusBadge}>
            <FiCheck size={isMobile ? 10 : 12} />
            {userData.status?.toUpperCase() || 'ACTIVE'}
          </div>
        </div>
      </div>

      {/* Personal Details */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <FiUser style={styles.sectionIcon} />
            Personal Details
          </h4>
        </div>
        <div style={styles.formGrid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <input 
                type="text" 
                value={formData.first_name || ''} 
                disabled={!isEditing}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Last Name</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <input 
                type="text" 
                value={formData.surname || ''} 
                disabled={!isEditing}
                onChange={(e) => handleInputChange('surname', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Other Name</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <input 
                type="text" 
                value={formData.other_name || ''} 
                disabled={!isEditing}
                onChange={(e) => handleInputChange('other_name', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <div style={{ position: 'relative' }}>
              <FiMail style={styles.inputIcon} />
              <input
                type="email"
                value={formData.email || ''}
                disabled={!isEditing}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Phone Number</label>
            <div style={{ position: 'relative' }}>
              <FiPhone style={styles.inputIcon} />
              <input
                type="tel"
                value={formData.telephone_numbers?.[0] || ''}
                disabled={!isEditing}
                onChange={(e) => handlePhoneNumberChange(0, e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>WhatsApp Number</label>
            <div style={{ position: 'relative' }}>
              <FiPhone style={styles.inputIcon} />
              <input
                type="tel"
                value={formData.whatsapp_number || ''}
                disabled={!isEditing}
                onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Date of Birth</label>
            <div style={{ position: 'relative' }}>
              <FiCalendar style={styles.inputIcon} />
              <input
                type="date"
                value={formData.date_of_birth || ''}
                disabled={!isEditing}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Gender</label>
            <div style={{ position: 'relative' }}>
              <FiUser style={styles.inputIcon} />
              <select
                value={formData.gender || ''}
                disabled={!isEditing}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                style={styles.input}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div style={{...styles.inputGroup, gridColumn: '1 / -1'}}>
            <label style={styles.label}>Full House Address</label>
            <textarea
              value={formData.full_house_address || ''}
              disabled={!isEditing}
              onChange={(e) => handleInputChange('full_house_address', e.target.value)}
              style={styles.textarea}
              rows="3"
            />
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      {isEditing && (
        <div style={styles.card} className="card-hover fade-in">
          <div style={styles.sectionHeader}>
            <h4 style={styles.sectionTitle}>
              <FiKey style={styles.sectionIcon} />
              Change Password
            </h4>
            <p style={{color: colors.textMedium, fontSize: '14px', margin: '5px 0 0 0', fontStyle: 'italic'}}>
              Leave blank if you don't want to change password
            </p>
          </div>
          {passwordError && (
            <div style={{
              background: '#fef2f2',
              color: colors.error,
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: `1px solid ${colors.error}20`,
              fontSize: '14px'
            }}>
              {passwordError}
            </div>
          )}
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Current Password</label>
              <div style={{ position: 'relative' }}>
                <FiKey style={styles.inputIcon} />
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  placeholder="Enter current password"
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>New Password</label>
              <div style={{ position: 'relative' }}>
                <FiKey style={styles.inputIcon} />
                <input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => handlePasswordChange('password', e.target.value)}
                  placeholder="Enter new password"
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm New Password</label>
              <div style={{ position: 'relative' }}>
                <FiKey style={styles.inputIcon} />
                <input
                  type="password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => handlePasswordChange('password_confirmation', e.target.value)}
                  placeholder="Confirm new password"
                  style={styles.input}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Geographic Information */}
      {userData.geographic_info && (
        <div style={styles.card} className="card-hover fade-in">
          <div style={styles.sectionHeader}>
            <h4 style={styles.sectionTitle}>
              <FiGlobe style={styles.sectionIcon} />
              Geographic Information
            </h4>
          </div>
          <div style={styles.formGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>State</label>
              <input 
                type="text" 
                value={userData.geographic_info.state || ''} 
                disabled 
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Senatorial District</label>
              <input 
                type="text" 
                value={userData.geographic_info.sd || ''} 
                disabled 
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Federal Constituency</label>
              <input 
                type="text" 
                value={userData.geographic_info.fc || ''} 
                disabled 
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Local Government</label>
              <input 
                type="text" 
                value={userData.geographic_info.lg || ''} 
                disabled 
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Ward</label>
              <input 
                type="text" 
                value={userData.geographic_info.ward || ''} 
                disabled 
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Polling Unit</label>
              <input 
                type="text" 
                value={userData.geographic_info.pu || ''} 
                disabled 
                style={styles.input}
              />
            </div>
          </div>
        </div>
      )}

      {/* Admin Information */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <HiOutlineCog style={styles.sectionIcon} />
            Admin Information
          </h4>
        </div>
        <div style={styles.adminInfoGrid}>
          <div>
            <label style={styles.label}>Membership Category</label>
            <div style={{ position: 'relative' }}>
              <FiAward style={styles.inputIcon} />
              <input 
                type="text" 
                value={userData.membership_category || 'Volunteer'} 
                disabled 
                style={styles.input}
              />
            </div>
          </div>
          <div>
            <label style={styles.label}>Member Since</label>
            <div style={{ position: 'relative' }}>
              <FiCalendar style={styles.inputIcon} />
              <input 
                type="text" 
                value={new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} 
                disabled 
                style={styles.input}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={styles.label}>Permissions</label>
          <div style={styles.permissionsList}>
            {userData.permissions && Array.isArray(userData.permissions) ? (
              userData.permissions.slice(0, 6).map((permission, index) => (
                <span key={index} style={styles.permissionTag}>
                  {getPermissionIcon(permission)}
                  {permission.replace(/-/g, ' ')}
                </span>
              ))
            ) : (
              <>
                <span style={styles.permissionTag}>
                  <FiUsers size={12} />
                  User Management
                </span>
                <span style={styles.permissionTag}>
                  <FiFileText size={12} />
                  Content Moderation
                </span>
                <span style={styles.permissionTag}>
                  <FiSettings size={12} />
                  System Config
                </span>
              </>
            )}
          </div>
        </div>
        
        <div style={styles.cardActions}>
          <button 
            style={styles.editBtn} 
            className="btn-hover" 
            onClick={handleEditToggle}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : isEditing ? (
              <>
                <FiSave size={16} />
                Save Changes
              </>
            ) : (
              <>
                <FiEdit2 size={16} />
                Edit Profile
              </>
            )}
          </button>
          {isEditing && (
            <button 
              style={styles.cancelBtn} 
              className="btn-hover" 
              onClick={() => {
                setIsEditing(false);
                // Reset form data to original user data
                setFormData({
                  first_name: userData.first_name || '',
                  surname: userData.surname || '',
                  other_name: userData.other_name || '',
                  email: userData.email || '',
                  telephone_numbers: userData.telephone_numbers || [''],
                  whatsapp_number: userData.whatsapp_number || '',
                  date_of_birth: userData.date_of_birth || '',
                  gender: userData.gender || '',
                  full_house_address: userData.full_house_address || '',
                  state_code: userData.state_code || '',
                  sql_code: userData.sql_code || '',
                  fc_code: userData.fc_code || '',
                  lcda_code: userData.lcda_code || '',
                  lg_code: userData.lg_code || '',
                  ward_code: userData.ward_code || '',
                  pu_code: userData.pu_code || '',
                  membership_category: userData.membership_category || ''
                });
                // Reset password fields
                setPasswordData({
                  current_password: "",
                  password: "",
                  password_confirmation: ""
                });
                setPasswordError("");
              }}
            >
              <FiX size={16} />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Membership Card */}
      <div style={styles.card} className="card-hover fade-in">
        <div style={styles.sectionHeader}>
          <h4 style={styles.sectionTitle}>
            <HiOutlineIdentification style={styles.sectionIcon} />
            Membership Card
          </h4>
        </div>
        <div style={styles.membershipCardContent}>
          <div>
            <div style={styles.membershipIdSection}>
              <FiAward size={isMobile ? 16 : 20} />
              <span style={{ 
                fontSize: isMobile ? '0.7rem' : '0.75rem', 
                opacity: 0.8, 
                fontWeight: '600', 
                letterSpacing: '1px' 
              }}>
                ADMIN ID CARD
              </span>
            </div>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: isMobile ? '1.25rem' : '1.5rem', 
              fontWeight: '700' 
            }}>
              {userData.full_name}
            </h3>
            <p style={{ 
              margin: '0 0 16px 0', 
              opacity: 0.9, 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}>
              {userData.membership_id}
            </p>
            <div style={styles.membershipBadges}>
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '6px 12px',
                borderRadius: '12px',
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontWeight: '600',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <HiOutlineShieldCheck size={isMobile ? 10 : 12} />
                {userData.role?.replace('-', ' ').toUpperCase()}
              </span>
              <span style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '6px 12px',
                borderRadius: '12px',
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                fontWeight: '600',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <FiCheck size={isMobile ? 10 : 12} />
                {userData.status?.toUpperCase() || 'VERIFIED'}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              borderRadius: '50%',
              border: `3px solid rgba(255,255,255,0.3)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              margin: isMobile ? '0 auto 8px' : '0 auto 12px',
              overflow: 'hidden'
            }}>
              {getProfileImageSrc() ? (
                <img 
                  src={getProfileImageSrc()} 
                  alt="profile" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <HiOutlineUserCircle size={isMobile ? 24 : 32} />
              )}
            </div>
            <p style={{ 
              margin: '8px 0', 
              fontSize: isMobile ? '0.7rem' : '0.75rem', 
              opacity: 0.8 
            }}>
              Member Since {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>

      {/* Logout Button */}
      <div style={styles.logoutContainer} className="fade-in">
        <button 
          style={styles.logoutBtn} 
          className="logout-btn-hover" 
          onClick={handleLogout}
        >
          <FiLogOut size={16} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminProfile;